import {
  TrackingPageCustomHtmlBlock,
  trackingPageGridSpanToPixels,
} from "@redotech/order-tracking-common/tracking-page-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useContext, useEffect, useMemo } from "react";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import { TrackingPageRuntimeLayoutContext } from "../context/tracking-page-runtime-layout-context";

/**
 * For a Custom HTML block set to fit its content, reports the content height to
 * the live desktop layout, never below the block's saved height, so the blocks
 * below move with it. Undefined when the block keeps its saved height.
 */
export function useFitHeightReport(
  block: Pick<
    TrackingPageCustomHtmlBlock,
    "_id" | "fitHeightToContent" | "size"
  >,
): ((contentHeight: number) => void) | undefined {
  const { environment } = useRequiredContext(TrackingPageContext);
  const reportBlockHeight = useContext(
    TrackingPageRuntimeLayoutContext,
  )?.reportBlockHeight;
  const enabled =
    !!block.fitHeightToContent &&
    environment === TrackingPageEnvironment.CUSTOMER_PORTAL;
  const blockId = block._id;
  const minHeight = trackingPageGridSpanToPixels(block.size.height);

  useEffect(() => {
    if (!enabled || !reportBlockHeight) {
      return;
    }
    return () => reportBlockHeight(blockId, null);
  }, [blockId, enabled, reportBlockHeight]);

  return useMemo(
    () =>
      enabled && reportBlockHeight
        ? (contentHeight: number) =>
            reportBlockHeight(blockId, Math.max(minHeight, contentHeight))
        : undefined,
    [blockId, enabled, minHeight, reportBlockHeight],
  );
}
